import { Button, chakra, Flex, useColorModeValue } from '@chakra-ui/react';
import React from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';

import type { FormFields } from './types';
import type { Transaction } from 'types/api/transaction';
import type { CsvExportParams } from 'types/client/address';

import config from 'configs/app';
import type { ResourceName } from 'lib/api/resources';
import {
  getCsvExportDateTimeValue,
  getCsvExportDefaultRange,
  getCsvExportRangeError,
} from 'lib/date/csvExport';
import ReCaptcha from 'ui/shared/reCaptcha/ReCaptcha';

import CsvExportFormField from './CsvExportFormField';
import useCsvExport from './useCsvExport';

interface Props {
  hash?: string;
  resource: ResourceName;
  filterType?: CsvExportParams['filterType'] | null;
  filterValue?: CsvExportParams['filterValue'] | null;
  typeFilter?: string;
  tokenAddressHashFilter?: string;
  fileNameTemplate: string;
  exportType: CsvExportParams['type'] | undefined;
  submitLabel?: string;
  onComplete?: () => void;
  onValidationErrorChange?: (error?: string) => void;
  items?: Array<Transaction>;
}

const CsvExportForm = ({
  hash,
  resource,
  filterType,
  filterValue,
  typeFilter,
  tokenAddressHashFilter,
  fileNameTemplate,
  exportType,
  submitLabel = 'Download',
  onComplete,
  onValidationErrorChange,
  items,
}: Props) => {
  const currentDateTime = React.useMemo(() => new Date(), []);
  const defaultValues = React.useMemo(() => getCsvExportDefaultRange(currentDateTime), [ currentDateTime ]);
  const maxDateTime = React.useMemo(() => getCsvExportDateTimeValue(currentDateTime), [ currentDateTime ]);

  const formApi = useForm<FormFields>({
    mode: 'onChange',
    defaultValues,
  });
  const { handleSubmit, control, formState } = formApi;
  const fromValue = useWatch({ control, name: 'from' });
  const toValue = useWatch({ control, name: 'to' });
  const rangeError = getCsvExportRangeError(fromValue, toValue);

  React.useEffect(() => {
    onValidationErrorChange?.(rangeError);
  }, [ onValidationErrorChange, rangeError ]);

  const { onFormSubmit, recaptcha, isCurrentPageExport } = useCsvExport({
    hash,
    resource,
    filterType,
    filterValue,
    typeFilter,
    tokenAddressHashFilter,
    fileNameTemplate,
    exportType,
    onComplete,
    items,
  });

  const disabledBg = useColorModeValue('#E9ECEF', '#3D3B3B');
  const disabledColor = useColorModeValue('#999999', '#747474');

  const isSubmitDisabled = exportType !== 'holders' && (
    !fromValue?.date ||
    !fromValue?.time ||
    !toValue?.date ||
    !toValue?.time ||
    Boolean(rangeError)
  );

  return (
    <FormProvider { ...formApi }>
      <chakra.form
        noValidate
        onSubmit={ handleSubmit(onFormSubmit) }
      >
        <Flex direction="column" gap={ 4 } alignItems="stretch">
          { exportType !== 'holders' && (
            <Flex direction="column" gap={ 4 }>
              <CsvExportFormField name="from" maxDateTime={ maxDateTime }/>
              <CsvExportFormField name="to" maxDateTime={ maxDateTime }/>
            </Flex>
          ) }

          { config.features.csvExport.isEnabled && !isCurrentPageExport && <ReCaptcha ref={ recaptcha.ref }/> }

          <Flex justifyContent="flex-end">
            <Button
              type="submit"
              w="148px"
              h="48px"
              px="16px"
              py="18px"
              borderRadius="40px"
              border="2.16px solid"
              borderColor="rgba(255, 255, 255, 0.25)"
              bg="white"
              color="black"
              fontSize="16px"
              fontWeight={ 500 }
              lineHeight="19.04px"
              boxShadow="inset 0 0 11.52px rgba(248, 248, 248, 0.25)"
              backdropFilter="blur(8.64px)"
              _hover={{ bg: 'whiteAlpha.900' }}
              _active={{ bg: 'whiteAlpha.800' }}
              _disabled={{
                cursor: 'not-allowed',
                bg: disabledBg,
                color: disabledColor,
                borderColor: disabledBg,
                boxShadow: 'none',
                opacity: 1,
              }}
              isLoading={ formState.isSubmitting }
              loadingText={ submitLabel }
              isDisabled={ Boolean(isSubmitDisabled) }
            >
              { submitLabel }
            </Button>
          </Flex>
        </Flex>
      </chakra.form>
    </FormProvider>
  );
};

export default React.memo(CsvExportForm);
